import { ChevronDown, Calendar } from 'lucide-react'
// import { ChevronDownIcon } from 'lucide-react' // TODO: 개발 완료 후 주석 해제
import { useState, useMemo, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
// import { DayPicker } from 'react-day-picker' // TODO: 개발 완료 후 주석 해제
import 'react-day-picker/style.css'
import { Filter as badWordsFilterEng } from 'bad-words'
import badWordsFilterKor from 'badwords-ko'
import { useQueryClient } from '@tanstack/react-query'

import NormalTopBar from '@/components/common/bar/NormalTopBar'
import ToastMessage from '@/components/common/toast/ToastMessage'
import WideButton from '@/components/common/button/WideButton'
import CardOne from '@/components/common/card/CardOne'
import { useUserStore } from '@/store/userStore'
import type { GradeCode, InputGradeCode, SexCode } from '@/api/model/userType'
import { useUpdateUserInfoMutation } from '@/hooks/queries/user/mutation'
import NEISInput from '@/components/common/input/NEISInput'
import { useUserInfoQuery } from '@/hooks/queries/user'

// Define MBTI types for clarity
type MbtiIE = 'I' | 'E'
type MbtiNS = 'N' | 'S'
type MbtiTF = 'T' | 'F'
type MbtiPJ = 'P' | 'J'

interface MbtiState {
  ie?: MbtiIE
  ns?: MbtiNS
  tf?: MbtiTF
  pj?: MbtiPJ
}

// Helper component for MBTI buttons
interface MbtiButtonProps {
  value: string
  currentValue?: string
  onClick: () => void
  children: React.ReactNode
}

const nicknameFilterEng = new badWordsFilterEng()
const nicknameFilterKor = new badWordsFilterKor()

// 닉네임 검증 함수 (프로필 수정용)
const validateNickname = (
  nickname: string
): {
  valid: boolean
  error: string
} => {
  if (nickname.trim().length === 0) {
    return { valid: false, error: '닉네임을 입력해주세요' }
  }

  if (nickname.length < 2) {
    return { valid: false, error: '두 글자 이상 입력하세요' }
  }

  // 영어 필터는 소문자로, 한글 필터는 원본으로 검사
  const lowerNickname = nickname.toLowerCase()
  const filteredValueEng = nicknameFilterEng.clean(lowerNickname)
  const filteredValueKor = nicknameFilterKor.clean(nickname)

  if (filteredValueEng.includes('*') || filteredValueKor.includes('*')) {
    return { valid: false, error: '부적절한 단어가 포함되어 있습니다.' }
  }

  if (['admin', 'administrator', 'root', 'system', 'null', 'undefined'].some(word => lowerNickname.includes(word))) {
    return { valid: false, error: '사용할 수 없는 닉네임입니다.' }
  }

  return { valid: true, error: '' }
}

const MbtiButton = ({ value, currentValue, onClick, children }: MbtiButtonProps) => (
  <button
    type='button'
    onClick={onClick}
    className={`flex-1 rounded-lg text-xs h-8 transition-colors duration-200 font-medium ${currentValue === value
      ? 'bg-amber-100 border-amber-400 text-amber-800 border'
      : 'bg-white border-gray-300 text-gray-700 border hover:bg-gray-50'
      }`}
  >
    {children}
  </button>
)

const gradeOptions = [
  { value: '', label: '선택하세요', disabled: true },
  // { value: 'g1', label: '초등학교 1학년' },
  // { value: 'g2', label: '초등학교 2학년' },
  // { value: 'g3', label: '초등학교 3학년' },
  { value: 'g17', label: '기타' },
  { value: 'g16', label: '검정' },
  { value: 'g15', label: '재수' },
  // { value: 'g14', label: '(중)검정' },
  // { value: 'g13', label: '(중)재수' },
  { value: 'g12', label: '고등학교 3학년' },
  { value: 'g11', label: '고등학교 2학년' },
  { value: 'g10', label: '고등학교 1학년' },
  { value: 'g9', label: '중학교 3학년' },
  { value: 'g8', label: '중학교 2학년' },
  { value: 'g7', label: '중학교 1학년' },
  { value: 'g6', label: '초등학교 6학년' },
  { value: 'g5', label: '초등학교 5학년' },
  { value: 'g4', label: '초등학교 4학년' }
]

const ProfileSettingPage = () => {
  const userStore = useUserStore()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  
  // States
  const [nickname, setNickname] = useState('')
  const [gender, setGender] = useState<SexCode | null>(null)
  const [birthday, setBirthday] = useState('1990-01-01')
  const [grade, setGrade] = useState<InputGradeCode>('g4')
  const [schoolName, setSchoolName] = useState('')
  const [schoolCode, setSchoolCode] = useState('')
  const [mbti, setMbti] = useState<MbtiState>({})
  const [isToastVisible, setIsToastVisible] = useState(false)
  
  // 드롭다운 관련 상태 추가
  const [isGradeDropdownOpen, setIsGradeDropdownOpen] = useState(false)
  const [selectedGradeIndex, setSelectedGradeIndex] = useState(-1)
  
  // 생일 달력 관련 상태 추가
  const [isBirthdayCalendarOpen, setIsBirthdayCalendarOpen] = useState(false)
  const [selectedYear, setSelectedYear] = useState<number | null>(null)
  // TODO: 개발 완료 후 주석 해제
  // const [selectedMonth, setSelectedMonth] = useState<number | null>(null)
  // const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined)
  // const [isYearOpen, setIsYearOpen] = useState(false)
  // const [isMonthOpen, setIsMonthOpen] = useState(false)

  // 드롭다운 참조 추가
  const gradeDropdownRef = useRef<HTMLDivElement>(null)
  const gradeSelectRef = useRef<HTMLDivElement>(null)
  // TODO: 개발 완료 후 주석 해제
  // const yearDropdownRef = useRef<HTMLDivElement>(null)
  // const monthDropdownRef = useRef<HTMLDivElement>(null)
  // const yearSelectRef = useRef<HTMLDivElement>(null)
  // const monthSelectRef = useRef<HTMLDivElement>(null)

  // API queries and mutations
  const { data: userInfo } = useUserInfoQuery(userStore.userInfo.user_id)
  const { mutate: updateUserInfo } = useUpdateUserInfoMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userInfo', userStore.userInfo.user_id] })
      navigate('/my')
    }
  })

  // 학년에 따른 학교 유형 및 검색 활성화 여부 결정
  const getSchoolConfig = (gradeCode: GradeCode) => {
    if (['g1', 'g2', 'g3', 'g4', 'g5', 'g6'].includes(gradeCode)) {
      return { schoolTypeFilter: '초등학교', isSchoolSearchEnabled: true }
    } else if (['g7', 'g8', 'g9'].includes(gradeCode)) {
      return { schoolTypeFilter: '중학교', isSchoolSearchEnabled: true }
    } else if (['g10', 'g11', 'g12'].includes(gradeCode)) {
      return { schoolTypeFilter: '고등학교', isSchoolSearchEnabled: true }
    } else {
      return { schoolTypeFilter: undefined, isSchoolSearchEnabled: false }
    }
  }

  const schoolConfig = useMemo(() => getSchoolConfig(grade), [grade])

  // Initial profile data from API
  const initialProfile = useMemo(() => {
    const ie = userInfo?.data.mbti?.charAt(0) as MbtiIE | undefined
    const ns = userInfo?.data.mbti?.charAt(1) as MbtiNS | undefined
    const tf = userInfo?.data.mbti?.charAt(2) as MbtiTF | undefined
    const pj = userInfo?.data.mbti?.charAt(3) as MbtiPJ | undefined

    return {
      nickname: userInfo?.data.nickname || '',
      gender: userInfo?.data.sex_code || null,
      birthday: userInfo?.data.birthday
        ? `${userInfo.data.birthday.slice(0, 4)}-${userInfo.data.birthday.slice(4, 6)}-${userInfo.data.birthday.slice(6, 8)}`
        : '1990-01-01',
      grade: userInfo?.data.grade_code || 'g4',
      schoolName: userInfo?.data.school_name || '',
      schoolCode: userInfo?.data.school_code || '',
      mbti: { ie, ns, tf, pj },
      mbtiString: [ie, ns, tf, pj].filter(Boolean).join('')
    }
  }, [userInfo])

  // Initialize form with user data
  useEffect(() => {
    if (userInfo?.data) {
      setNickname(initialProfile.nickname)
      setGender(initialProfile.gender)
      setBirthday(initialProfile.birthday)
      setGrade(initialProfile.grade as InputGradeCode)
      setSchoolName(initialProfile.schoolName)
      setSchoolCode(initialProfile.schoolCode)
      setMbti(initialProfile.mbti)
    }
  }, [initialProfile, userInfo])

  // 드롭다운 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        gradeDropdownRef.current &&
        !gradeDropdownRef.current.contains(event.target as Node) &&
        gradeSelectRef.current &&
        !gradeSelectRef.current.contains(event.target as Node)
      ) {
        setIsGradeDropdownOpen(false)
        setSelectedGradeIndex(-1)
      }

      // TODO: 개발 완료 후 주석 해제 - 생일 달력 드롭다운 외부 클릭 감지
      // if (
      //   yearDropdownRef.current &&
      //   !yearDropdownRef.current.contains(event.target as Node) &&
      //   yearSelectRef.current &&
      //   !yearSelectRef.current.contains(event.target as Node)
      // ) {
      //   setIsYearOpen(false)
      // }

      // if (
      //   monthDropdownRef.current &&
      //   !monthDropdownRef.current.contains(event.target as Node) &&
      //   monthSelectRef.current &&
      //   !monthSelectRef.current.contains(event.target as Node)
      // ) {
      //   setIsMonthOpen(false)
      // }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // 학년 변경시 학교 정보 초기화
  useEffect(() => {
    if (!schoolConfig.isSchoolSearchEnabled) {
      setSchoolName('')
      setSchoolCode('')
    }
  }, [schoolConfig.isSchoolSearchEnabled])

  // Handlers
  const handleMbtiChange = (dimension: keyof MbtiState, value: MbtiIE | MbtiNS | MbtiTF | MbtiPJ) => {
    setMbti(prev => {
      const newMbti = { ...prev }
      if (newMbti[dimension] === value) {
        delete newMbti[dimension]
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        newMbti[dimension] = value as any
      }
      return newMbti
    })
  }

  const handleGradeChange = (newGrade: InputGradeCode) => {
    setGrade(newGrade)

    // 학교 검색이 비활성화되는 학년이거나, 학교 유형이 바뀌는 경우 학교 정보 초기화
    const newConfig = getSchoolConfig(newGrade)
    const currentConfig = getSchoolConfig(grade)

    if (!newConfig.isSchoolSearchEnabled || newConfig.schoolTypeFilter !== currentConfig.schoolTypeFilter) {
      setSchoolName('')
      setSchoolCode('')
    }
  }

  // 드롭다운 키보드 이벤트 처리
  const handleGradeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (selectedGradeIndex >= 0 && !gradeOptions[selectedGradeIndex].disabled) {
        selectGradeOption(gradeOptions[selectedGradeIndex])
      } else {
        setIsGradeDropdownOpen(!isGradeDropdownOpen)
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (isGradeDropdownOpen) {
        const nextIndex = selectedGradeIndex < gradeOptions.length - 1 ? selectedGradeIndex + 1 : 0
        setSelectedGradeIndex(nextIndex)
      } else {
        setIsGradeDropdownOpen(true)
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (isGradeDropdownOpen) {
        const prevIndex = selectedGradeIndex > 0 ? selectedGradeIndex - 1 : gradeOptions.length - 1
        setSelectedGradeIndex(prevIndex)
      }
    } else if (e.key === 'Escape') {
      setIsGradeDropdownOpen(false)
      setSelectedGradeIndex(-1)
    }
  }

  // 드롭다운 옵션 선택
  const selectGradeOption = (option: (typeof gradeOptions)[0]) => {
    if (!option.disabled) {
      handleGradeChange(option.value as InputGradeCode)
      setIsGradeDropdownOpen(false)
      setSelectedGradeIndex(-1)
    }
  }

  // 생일 달력 관련 로직
  const currentYear = new Date().getFullYear()
  const maxYear = currentYear

  // 년도 옵션 생성 (최신년도부터)
  const yearOptions = Array.from({ length: 101 }, (_, i) => maxYear - i)

  // TODO: 개발 완료 후 주석 해제
  // const today = new Date()
  // const minYear = currentYear - 100
  // const monthOptions = Array.from({ length: 12 }, (_, i) => ({
  //   value: i + 1,
  //   label: `${i + 1}월`
  // }))

  // 생일 초기화 (userInfo 기반)
  useEffect(() => {
    if (userInfo?.data && birthday) {
      const date = new Date(birthday)
      // TODO: 개발 완료 후 주석 해제
      // setSelectedDate(date)
      setSelectedYear(date.getFullYear())
      // setSelectedMonth(date.getMonth() + 1)
    }
  }, [birthday, userInfo])

  // TODO: 개발 완료 후 주석 해제 - 생일 관련 핸들러들
  // const handleDateSelect = (date: Date | undefined) => {
  //   setSelectedDate(date)
  //   if (date) {
  //     const year = date.getFullYear()
  //     const month = String(date.getMonth() + 1).padStart(2, '0')
  //     const day = String(date.getDate()).padStart(2, '0')
  //     const dateString = `${year}-${month}-${day}`
  //     setBirthday(dateString)
  //   }
  //   setIsBirthdayCalendarOpen(false)
  // }

  const handleYearSelect = (year: number) => {
    setSelectedYear(year)
    // TODO: 개발 완료 후 원래 로직으로 복원
    // setIsYearOpen(false)
    // 임시: 연도만 받고 월/일은 1월 1일로 고정
    const dateString = `${year}-01-01`
    setBirthday(dateString)
    setIsBirthdayCalendarOpen(false)

    // if (selectedMonth) {
    //   const newDate = new Date(year, selectedMonth - 1, 1)
    //   setSelectedDate(newDate)
    // }
  }

  // TODO: 개발 완료 후 주석 해제
  // const handleMonthSelect = (month: number) => {
  //   setSelectedMonth(month)
  //   setIsMonthOpen(false)
  //   if (selectedYear) {
  //     const newDate = new Date(selectedYear, month - 1, 1)
  //     setSelectedDate(newDate)
  //   }
  // }

  // const formatDisplayDate = (date: Date) => {
  //   return date.toLocaleDateString('ko-KR', {
  //     year: 'numeric',
  //     month: 'long',
  //     day: 'numeric'
  //   })
  // }

  // const calendarMonth = selectedYear && selectedMonth ? new Date(selectedYear, selectedMonth - 1, 1) : undefined

  // const getMonthNameKo = (month: number) => {
  //   const monthNames = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월']
  //   return monthNames[month - 1]
  // }

  // 닉네임 검증
  const nicknameValidation = useMemo(() => validateNickname(nickname), [nickname])

  // MBTI 관련 계산
  const mbtiLength = useMemo(() => Object.values(mbti).filter(Boolean).length, [mbti])
  const currentMbtiString = useMemo(() => [mbti.ie, mbti.ns, mbti.tf, mbti.pj].filter(Boolean).join(''), [mbti])

  // Validation logic
  const isChanged = useMemo(() => {
    return (
      nickname !== initialProfile.nickname ||
      gender !== initialProfile.gender ||
      birthday !== initialProfile.birthday ||
      grade !== initialProfile.grade ||
      schoolName !== initialProfile.schoolName ||
      schoolCode !== initialProfile.schoolCode ||
      currentMbtiString !== initialProfile.mbtiString
    )
  }, [nickname, gender, birthday, grade, schoolName, schoolCode, currentMbtiString, initialProfile])

  const isValid = useMemo(() => {
    const isNicknameValid = nicknameValidation.valid
    const isBirthdayValid = birthday.length === 10 // YYYY-MM-DD 형태

    // 학교 검증: 학교명이 입력되어 있으면 반드시 학교를 선택했어야 함 (학교코드 필요)
    // 단, 학교 검색이 비활성화된 학년(g13~g17)은 예외
    const isSchoolValid = schoolConfig.isSchoolSearchEnabled
      ? schoolName.trim() === '' || schoolCode !== '' // 빈칸이거나 학교를 선택했어야 함
      : true // 학교 검색 비활성화 학년은 항상 유효

    // MBTI는 선택사항이므로 1~3개 선택되어도 다른 수정사항이 있으면 저장 가능
    return isNicknameValid && isSchoolValid && isBirthdayValid
  }, [nicknameValidation, schoolName, schoolCode, schoolConfig.isSchoolSearchEnabled, birthday])

  const canSave = isChanged && isValid

  const handleSave = () => {
    if (!canSave) return

    // MBTI가 4개 모두 선택되었거나 0개 선택된 경우에만 업데이트
    // 1~3개 선택된 경우는 초기값 유지
    const body = {
      nickname,
      sex_code: gender,
      birthday: birthday.replace(/-/g, ''),
      grade_code: grade,
      school_code: schoolCode,
      school_name: schoolName,
      mbti: mbtiLength === 0 || mbtiLength === 4 ? currentMbtiString : initialProfile.mbtiString,
      type_id: userInfo?.data.type16_id ?? null
    }

    updateUserInfo({ user_id: userStore.userInfo.user_id, body })
  }

  // Constants
  const email = userInfo?.data.email ?? ''
  const inputStyles =
    'w-full py-2 px-3 border border-gray-300 rounded-lg focus:ring-amber-400 focus:border-amber-400 transition text-sm'
  const sectionStyles = 'bg-white rounded-2xl flex flex-col gap-2 px-3 py-5'
  const labelStyles = 'block text-sm font-medium text-gray-600'

  return (
    <div className='flex flex-col h-screen overflow-y-auto'>
      <ToastMessage
        message='프로필이 저장되었습니다!'
        isVisible={isToastVisible}
        onClose={() => setIsToastVisible(false)}
      />

      <NormalTopBar
        title='프로필 설정'
        prevButton
        onPrevButtonClick={() => navigate('/my')}
        textClassName='text-xl text-text-header font-semibold'
        boxClassName='bg-gradient-to-r from-amber-400 to-yellow-300'
      />

      <div className='flex-1 p-3 space-y-3'>
        {/* 닉네임 섹션 */}
        <CardOne className={sectionStyles}>
          <label htmlFor='nickname' className={labelStyles}>
            닉네임
          </label>
          <input
            id='nickname'
            type='text'
            value={nickname}
            onChange={e => setNickname(e.target.value)}
            className={inputStyles}
          />
          <div className='h-7'>
            <p className='text-sm text-gray-500 pt-1'>{email}</p>
            {nicknameValidation.error && <p className='text-red-500 text-xs'>{nicknameValidation.error}</p>}
          </div>
        </CardOne>

        {/* 성별 섹션 */}
        <CardOne className={sectionStyles}>
          <p className={labelStyles}>성별</p>
          <div className='flex space-x-3'>
            <button
              type='button'
              onClick={() => setGender('male')}
              className={`flex-1 rounded-lg h-8 transition-colors duration-200 text-xs font-medium ${gender === 'male'
                ? 'bg-amber-100 border-amber-400 text-amber-800 border'
                : 'bg-white border-gray-300 text-gray-700 border hover:bg-gray-50'
                }`}
            >
              남자
            </button>
            <button
              type='button'
              onClick={() => setGender('female')}
              className={`flex-1 rounded-lg h-8 transition-colors duration-200 text-xs font-medium ${gender === 'female'
                ? 'bg-amber-100 border-amber-400 text-amber-800 border'
                : 'bg-white border-gray-300 text-gray-700 border hover:bg-gray-50'
                }`}
            >
              여자
            </button>
            <button
              type='button'
              onClick={() => setGender('other')}
              className={`flex-1 rounded-lg h-8 transition-colors duration-200 text-xs font-medium ${gender === 'other'
                ? 'bg-amber-100 border-amber-400 text-amber-800 border'
                : 'bg-white border-gray-300 text-gray-700 border hover:bg-gray-50'
                }`}
            >
              기타
            </button>
          </div>
        </CardOne>

        {/* 생일 섹션 */}
        <CardOne className={sectionStyles}>
          {/* TODO: 개발 완료 후 "생일"로 복원 */}
          <label htmlFor='birthday' className={labelStyles}>
            출생 연도
          </label>
          <div className='relative'>
            <button
              type='button'
              onClick={() => setIsBirthdayCalendarOpen(!isBirthdayCalendarOpen)}
              className={`${inputStyles} appearance-none cursor-pointer flex items-center justify-between`}
            >
              <div className='flex items-center gap-2'>
                <Calendar className='w-4 h-4 text-amber-500' />
                {/* TODO: 개발 완료 후 원래 로직으로 복원 - selectedDate ? formatDisplayDate(selectedDate) : '날짜를 선택하세요' */}
                <span>{selectedYear ? `${selectedYear}년` : '출생 연도를 선택하세요'}</span>
              </div>
            </button>

            {/* 달력 모달 */}
            {isBirthdayCalendarOpen && (
              <>
                {/* 커스텀 캘린더 스타일 */}
                <style>{`
                  .profile-calendar .rdp-day_selected {
                    background-color: #f59e0b;
                    color: white;
                  }
                  .profile-calendar .rdp-day_today {
                    background-color: rgba(245, 158, 11, 0.2);
                    color: #f59e0b;
                  }
                  .profile-calendar .rdp-day:hover {
                    background-color: rgba(245, 158, 11, 0.1);
                  }
                  
                  /* 기본 캡션 숨기기 */
                  .profile-calendar .rdp-month_caption {
                    visibility: hidden;
                    pointer-events: none;
                  }
                  
                  /* 달력 컨테이너에 상대 위치 지정 */
                  .profile-calendar-container {
                    position: relative;
                  }
                  .profile-calendar .rdp-nav {
                    display: none;
                  }
                  .profile-calendar .rdp-day.rdp-selected > .rdp-day_button {
                    border: 1px solid #f59e0b;
                    color: white;
                    background-color: #f59e0b;
                  }
                  .profile-calendar .rdp-day.rdp-today > .rdp-day_button {
                    font-weight: bold;
                  }
                `}</style>

                <div className='fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4'>
                  <div className='bg-white rounded-2xl shadow-xl w-full max-w-sm'>
                    {/* 헤더 */}
                    <div className='bg-amber-500 text-white text-center py-4 rounded-t-2xl'>
                      {/* TODO: 개발 완료 후 "생년월일 선택"으로 복원 */}
                      <h3 className='text-lg font-bold'>출생 연도 선택</h3>
                    </div>

                    {/* TODO: 개발 완료 후 원래 UI로 복원 - 현재는 연도만 선택 */}
                    <div className='p-4'>
                      <div className='max-h-64 overflow-y-auto'>
                        {yearOptions.map(year => (
                          <div
                            key={year}
                            onClick={() => handleYearSelect(year)}
                            className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-amber-50 text-center text-gray-900 font-medium ${selectedYear === year ? 'bg-amber-100 text-amber-700' : ''}`}
                          >
                            {year}년
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* 원래 달력 UI - TODO: 개발 완료 후 주석 해제
                    <div className='p-4'>
                      <div className='profile-calendar-container profile-calendar'>
                        <div className='absolute top-0 left-0 right-0 flex items-center justify-between h-12 z-10 px-2'>
                          <div className='flex items-center gap-2'>
                            <div className='relative'>
                              <div
                                ref={yearSelectRef}
                                onClick={() => setIsYearOpen(!isYearOpen)}
                                className='px-3 py-2 bg-white/70 backdrop-blur-sm transition-all duration-300 text-center text-sm font-medium cursor-pointer flex items-center gap-1'
                              >
                                <span className={!selectedYear ? 'text-gray-400' : 'text-gray-800'}>
                                  {selectedYear ? `${selectedYear}년` : '년'}
                                </span>
                                <ChevronDownIcon
                                  className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isYearOpen ? 'rotate-180' : ''}`}
                                />
                              </div>
                              {isYearOpen && (
                                <div
                                  ref={yearDropdownRef}
                                  className='absolute z-20 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto min-w-[100px]'
                                >
                                  {yearOptions.map(year => (
                                    <div
                                      key={year}
                                      onClick={() => handleYearSelect(year)}
                                      className='px-4 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-50 text-center text-gray-900 font-medium text-sm'
                                    >
                                      {year}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className='relative'>
                              <div
                                ref={monthSelectRef}
                                onClick={() => setIsMonthOpen(!isMonthOpen)}
                                className='px-3 py-2 bg-white/70 backdrop-blur-sm transition-all duration-300 text-center text-sm font-medium cursor-pointer flex items-center gap-1'
                              >
                                <span className={!selectedMonth ? 'text-gray-400' : 'text-gray-800'}>
                                  {selectedMonth ? getMonthNameKo(selectedMonth) : '월'}
                                </span>
                                <ChevronDownIcon
                                  className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isMonthOpen ? 'rotate-180' : ''}`}
                                />
                              </div>
                              {isMonthOpen && (
                                <div
                                  ref={monthDropdownRef}
                                  className='absolute z-20 left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto min-w-[140px]'
                                >
                                  {monthOptions.map(month => (
                                    <div
                                      key={month.value}
                                      onClick={() => handleMonthSelect(month.value)}
                                      className='px-4 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-50 text-left text-gray-900 font-medium text-sm'
                                    >
                                      {getMonthNameKo(month.value)}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <DayPicker
                          mode='single'
                          selected={selectedDate}
                          onSelect={handleDateSelect}
                          month={calendarMonth}
                          disabled={[{ before: new Date(1924, 0, 1) }, { after: today }]}
                          required
                        />
                      </div>
                    </div>
                    */}

                    {/* 닫기 버튼 */}
                    <div className='p-4 border-t'>
                      <button
                        type='button'
                        onClick={() => setIsBirthdayCalendarOpen(false)}
                        className='w-full py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm'
                      >
                        닫기
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </CardOne>

        {/* 학년 및 학교 섹션 */}
        <CardOne className={sectionStyles}>
          <p className={labelStyles}>학년</p>
          <div className='relative'>
            <div
              ref={gradeSelectRef}
              onClick={() => setIsGradeDropdownOpen(!isGradeDropdownOpen)}
              onKeyDown={handleGradeKeyDown}
              tabIndex={0}
              className={`${inputStyles} appearance-none cursor-pointer flex items-center justify-between`}
            >
              <span className={!grade ? 'text-gray-400' : 'text-gray-800'}>
                {gradeOptions.find(option => option.value === grade)?.label || '선택하세요'}
              </span>
              <ChevronDown
                className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isGradeDropdownOpen ? 'rotate-180' : ''
                  }`}
              />
            </div>

            {/* 드롭다운 리스트 */}
            {isGradeDropdownOpen && (
              <div
                ref={gradeDropdownRef}
                className='absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto'
              >
                {gradeOptions.slice(1).map((option, index) => (
                  <div
                    key={option.value}
                    onClick={() => selectGradeOption(option)}
                    className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-50 ${selectedGradeIndex === index + 1 ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
                      } ${option.disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                  >
                    <div className='font-medium text-center text-sm'>{option.label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {schoolConfig.isSchoolSearchEnabled ? (
            <>
              <NEISInput
                inputClassName={inputStyles}
                placeholder={`${schoolConfig.schoolTypeFilter}명을 검색하세요`}
                value={schoolName}
                onChange={setSchoolName}
                onSelect={school => {
                  setSchoolName(school.school_name)
                  setSchoolCode(school.school_code)
                }}
                schoolTypeFilter={schoolConfig.schoolTypeFilter}
              />
              {schoolName.trim() !== '' && schoolCode === '' && (
                <p className='text-red-500 text-xs mt-1'>검색 결과에서 학교를 선택해주세요.</p>
              )}
            </>
          ) : (
            <input
              type='text'
              value=''
              placeholder='학교 선택이 필요하지 않습니다'
              className={`${inputStyles} bg-gray-100 cursor-not-allowed`}
              disabled
              readOnly
            />
          )}
        </CardOne>

        {/* MBTI 섹션 */}
        <CardOne className={sectionStyles}>
          <p className={labelStyles}>MBTI (선택사항)</p>
          <div className='space-y-3'>
            <div className='grid grid-cols-2 gap-x-3'>
              <MbtiButton value='I' currentValue={mbti.ie} onClick={() => handleMbtiChange('ie', 'I')}>
                I
              </MbtiButton>
              <MbtiButton value='E' currentValue={mbti.ie} onClick={() => handleMbtiChange('ie', 'E')}>
                E
              </MbtiButton>
            </div>
            <div className='grid grid-cols-2 gap-x-3'>
              <MbtiButton value='N' currentValue={mbti.ns} onClick={() => handleMbtiChange('ns', 'N')}>
                N
              </MbtiButton>
              <MbtiButton value='S' currentValue={mbti.ns} onClick={() => handleMbtiChange('ns', 'S')}>
                S
              </MbtiButton>
            </div>
            <div className='grid grid-cols-2 gap-x-3'>
              <MbtiButton value='T' currentValue={mbti.tf} onClick={() => handleMbtiChange('tf', 'T')}>
                T
              </MbtiButton>
              <MbtiButton value='F' currentValue={mbti.tf} onClick={() => handleMbtiChange('tf', 'F')}>
                F
              </MbtiButton>
            </div>
            <div className='grid grid-cols-2 gap-x-3'>
              <MbtiButton value='P' currentValue={mbti.pj} onClick={() => handleMbtiChange('pj', 'P')}>
                P
              </MbtiButton>
              <MbtiButton value='J' currentValue={mbti.pj} onClick={() => handleMbtiChange('pj', 'J')}>
                J
              </MbtiButton>
            </div>
          </div>
        </CardOne>
      </div>

      {/* 저장 버튼 */}
      <div className='px-4 pb-6'>
        <WideButton
          handleClick={handleSave}
          buttonText='저장'
          isDisabled={!canSave}
          className={`${!canSave
            ? 'bg-gray-300 cursor-not-allowed border-gray-300 text-gray-500'
            : 'text-white bg-amber-500 border-amber-500'
            }`}
        />
      </div>
    </div>
  )
}

export default ProfileSettingPage
